import React from 'react';
import { 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Avatar, 
  Tabs, 
  Tab, 
  LinearProgress 
} from '@material-ui/core';
import { Table, Link } from "@backstage/core-components";
import { 
  Repository, 
  RepositoryHealthDetails, 
  BranchInfo, 
  Contributor 
} from '../types';
import { 
  getHealthStatusIcon, 
  getHealthStatusChip, 
  formatDate, 
  formatDateShort, 
  formatBytes, 
  formatNumber, 
  formatDuration, 
  getPRStatusColor 
} from '../utils';
import { TabPanel } from '../shared';

// Repository list table component
interface RepositoryListProps {
  repositories: Repository[];
  onRepositorySelect: (repository: Repository) => void;
}

export const RepositoryList: React.FC<RepositoryListProps> = ({ 
  repositories, 
  onRepositorySelect 
}) => {
  const columns = [
    { 
      title: 'Name', 
      field: 'name',
      render: (rowData: Repository) => (
        <Button 
          color="primary" 
          style={{ textTransform: 'none', padding: 0, minWidth: 'auto' }}
          onClick={() => onRepositorySelect(rowData)}
        >
          {rowData.name}
        </Button>
      )
    },
    { 
      title: 'ID', 
      field: 'id' 
    },
    { 
      title: 'Repository URL', 
      field: 'remoteUrl',
      render: (rowData: Repository) => (
        <Link 
          to={rowData.remoteUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {rowData.remoteUrl}
        </Link>
      )
    },
    { 
      title: 'Default Branch', 
      field: 'defaultBranch' 
    },
  ];

  return (
    <Table
      title="Repositories"
      options={{ search: true, paging: true }}
      columns={columns}
      data={repositories}
    />
  );
};

// Repository detail view component
interface RepositoryDetailProps {
  repository: Repository;
  repoHealthDetails: RepositoryHealthDetails;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
}

export const RepositoryDetail: React.FC<RepositoryDetailProps> = ({ 
  repository, 
  repoHealthDetails, 
  tabValue, 
  onTabChange 
}) => {
  // Branch columns for table
  const branchColumns = [
    {
      title: 'Branch Name',
      field: 'name',
      render: (rowData: BranchInfo) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{rowData.name}</span>
          {rowData.isDefault && (
            <Chip 
              size="small" 
              label="Default" 
              style={{ backgroundColor: '#2196f3', color: 'white' }}
            />
          )}
        </div>
      )
    },
    {
      title: 'Total Commits',
      field: 'commitCount'
    },
    {
      title: 'Created By',
      field: 'createdBy',
      render: (rowData: BranchInfo) => (
        <div>
          <div><strong>{rowData.createdBy.name}</strong></div>
          <small style={{ color: '#666' }}>{rowData.createdBy.email}</small>
          <br />
          <small style={{ color: '#999' }}>{formatDate(rowData.createdBy.date)}</small>
        </div>
      )
    },
    {
      title: 'Contributors',
      field: 'contributors',
      render: (rowData: BranchInfo) => (
        <Chip 
          size="small" 
          label={`${rowData.contributors.length} contributors`}
          variant="outlined"
        />
      )
    },
    {
      title: 'Last Commit',
      field: 'lastCommitDate',
      render: (rowData: BranchInfo) => formatDate(rowData.lastCommitDate)
    }
  ];

  // Contributor columns for table
  const contributorColumns = [
    {
      title: 'Contributor',
      field: 'name',
      render: (rowData: Contributor) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar 
            src={rowData.avatar} 
            style={{ width: 24, height: 24 }}
          >
            {rowData.name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div><strong>{rowData.name}</strong></div>
            <small style={{ color: '#666' }}>{rowData.email}</small>
          </div>
        </div>
      )
    },
    {
      title: 'Commit Count',
      field: 'commitCount'
    },
    {
      title: 'Last Commit',
      field: 'lastCommitDate',
      render: (rowData: Contributor) => formatDate(rowData.lastCommitDate)
    }
  ];

  return (
    <div>
      {/* Repository Header */}
      <Card style={{ marginBottom: '20px' }}>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
                {repoHealthDetails.name}
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
                <Link to={repoHealthDetails.remoteUrl} target="_blank" rel="noopener noreferrer">
                  {repoHealthDetails.remoteUrl}
                </Link>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Default Branch: <strong>{repoHealthDetails.defaultBranch}</strong> • 
                Size: <strong>{formatBytes(repoHealthDetails.size)}</strong> • 
                Created: <strong>{formatDateShort(repoHealthDetails.createdDate)}</strong>
              </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getHealthStatusIcon(repoHealthDetails.healthStatus)}
              {getHealthStatusChip(repoHealthDetails.healthStatus)}
              {repoHealthDetails.isArchived && (
                <Chip size="small" label="Archived" style={{ backgroundColor: '#9e9e9e', color: 'white' }} />
              )}
            </div>
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <Typography variant="h6">{formatNumber(repoHealthDetails.totalCommits)}</Typography>
                <Typography variant="body2" color="textSecondary">Total Commits</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <Typography variant="h6">{repoHealthDetails.totalContributors}</Typography>
                <Typography variant="body2" color="textSecondary">Contributors</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <Typography variant="h6">{repoHealthDetails.branches.length}</Typography>
                <Typography variant="body2" color="textSecondary">Branches</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <Typography variant="h6">{repoHealthDetails.daysSinceLastCommit}</Typography>
                <Typography variant="body2" color="textSecondary">Days Since Last Commit</Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Repository Details Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={onTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label="Branches" />
          <Tab label="Contributors" />
          <Tab label="Pull Requests" />
          <Tab label="Statistics" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Overview */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>
                    Repository Creation Info
                  </Typography>
                  {repoHealthDetails.creationInfo && (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <Typography variant="body2" color="textSecondary">Created By:</Typography>
                        <Typography variant="body1">
                          <strong>{repoHealthDetails.creationInfo.createdBy.name}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {repoHealthDetails.creationInfo.createdBy.email}
                        </Typography>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <Typography variant="body2" color="textSecondary">Creation Date:</Typography>
                        <Typography variant="body1">
                          {formatDate(repoHealthDetails.creationInfo.createdDate)}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">Initial Commit:</Typography>
                        <Typography variant="body1">
                          {repoHealthDetails.creationInfo.initialCommit.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {repoHealthDetails.creationInfo.initialCommit.id.substring(0, 8)}
                        </Typography>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>
                    Recent Activity
                  </Typography>
                  <div>
                    <Typography variant="body2" color="textSecondary">Last Commit:</Typography>
                    <Typography variant="body1">
                      {formatDate(repoHealthDetails.lastCommitDate)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                      Health Status: {getHealthStatusChip(repoHealthDetails.healthStatus)}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Branches */}
          <Table
            title={`Branches (${repoHealthDetails.branches.length})`}
            options={{ search: true, paging: true, pageSize: 10 }}
            columns={branchColumns}
            data={repoHealthDetails.branches}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Contributors */}
          <Table
            title={`Contributors (${repoHealthDetails.contributors.length})`}
            options={{ search: true, paging: true, pageSize: 10 }}
            columns={contributorColumns}
            data={repoHealthDetails.contributors}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Pull Requests */}
          {repoHealthDetails.pullRequestStats ? (
            <div>
              <Grid container spacing={3} style={{ marginBottom: '24px' }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent style={{ textAlign: 'center' }}>
                      <Typography variant="h5" style={{ color: '#2196f3' }}>
                        {repoHealthDetails.pullRequestStats.totalPRs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Total PRs</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent style={{ textAlign: 'center' }}>
                      <Typography variant="h5" style={{ color: '#4caf50' }}>
                        {repoHealthDetails.pullRequestStats.mergedPRs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Merged</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent style={{ textAlign: 'center' }}>
                      <Typography variant="h5" style={{ color: '#ff9800' }}>
                        {repoHealthDetails.pullRequestStats.openPRs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Open</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent style={{ textAlign: 'center' }}>
                      <Typography variant="h5" style={{ color: '#9c27b0' }}>
                        {formatDuration(repoHealthDetails.pullRequestStats.averageMergeTime)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Avg Merge Time</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>
                    Recent Pull Requests
                  </Typography>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {repoHealthDetails.pullRequestStats.recentPRs.slice(0, 50).map((pr: any, index: number) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: index < 9 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <div 
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: getPRStatusColor(pr.status)
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              #{pr.id} {pr.title}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              by {pr.author} • {pr.linesChanged} lines • {pr.filesChanged} files
                            </Typography>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Chip 
                            size="small" 
                            label={pr.status} 
                            style={{ 
                              backgroundColor: getPRStatusColor(pr.status), 
                              color: 'white',
                              marginBottom: '4px'
                            }}
                          />
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                            {new Date(pr.createdDate).toLocaleDateString()}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No pull request data available.
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {/* Statistics */}
          <Typography variant="body1" color="textSecondary">
            Statistics panel content would go here.
          </Typography>
        </TabPanel>
      </Card>
    </div>
  );
};